import React, { useState } from 'react';
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonBadge,
  IonButton,
  IonIcon,
  IonText,
  IonSpinner,
} from '@ionic/react';
import { createOutline, chevronDownOutline, chevronUpOutline } from 'ionicons/icons';
import { CAItem } from '@/types/model/corrective-action';
import { apiService } from '@/services/ApiService';
import { CorrectiveActionResponseEntry } from '@/types/response/CorrectiveActionResponses';
import lookupDataService, { isValidToStatusId } from '@/services/data/LookupDataService';

interface CACardProps {
  caItem: CAItem;
  onEdit: (caItem: CAItem) => void;
}

const CACard: React.FC<CACardProps> = ({ caItem, onEdit }) => {
  const [expanded, setExpanded] = useState(false);
  const [responses, setResponses] = useState<CorrectiveActionResponseEntry[] | null>(null);
  const [loadingResponses, setLoadingResponses] = useState(false);

  const CA_STATUSES = lookupDataService.getCorrectiveActionStatuses();
  const statusName =
    CA_STATUSES.find(s => s.Id === caItem.statusId)?.Name ?? `Status ${caItem.statusId}`;

  const CA_VALIDATION_STATUSES = lookupDataService.getCAValidationStatuses();
  const validationStatusId = isValidToStatusId(caItem.isValid);
  const validationStatusName = CA_VALIDATION_STATUSES.find(s => s.Id === validationStatusId)?.Name ?? '';
  const validationStatusColor = validationStatusId === 1 ? 'var(--ion-color-success)' : validationStatusId === 2 ? 'var(--ion-color-danger)' : 'inherit';

  const statusColor = (() => {
    switch (caItem.statusId) {
      case 1: case 2: return 'warning';
      case 3: case 4: return 'success';
      case 5: return 'primary';
      case 6: return 'danger';
      default: return 'medium';
    }
  })();

  const fmt = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

  const handleToggle = async () => {
    const wasExpanded = expanded;
    setExpanded(e => !e);
    if (!wasExpanded && responses === null) {
      setLoadingResponses(true);
      try {
        const data = await apiService.getCorrectiveActionResponses(caItem.id);
        setResponses(data);
      } catch {
        setResponses([]);
      } finally {
        setLoadingResponses(false);
      }
    }
  };

  const fieldRow: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '5px 0',
    borderBottom: '1px solid var(--ion-color-light-tint)',
    gap: '8px',
  };
  const lbl: React.CSSProperties = {
    fontSize: '0.75rem',
    fontWeight: '600',
    color: 'var(--ion-color-medium)',
    minWidth: '130px',
    flexShrink: 0,
  };
  const val: React.CSSProperties = { fontSize: '0.85rem', textAlign: 'right', flex: 1 };

  return (
    <IonCard style={{ marginBottom: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <IonCardHeader style={{ paddingBottom: '4px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
          <IonCardTitle style={{ fontSize: '0.95rem', flex: 1 }}>
            {caItem.issue}
          </IonCardTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
            <IonBadge color={statusColor} style={{ fontSize: '0.7rem', whiteSpace: 'normal' }}>
              {statusName}
            </IonBadge>
            <IonButton fill="clear" size="small" onClick={() => onEdit(caItem)}>
              <IonIcon icon={createOutline} />
            </IonButton>
          </div>
        </div>
      </IonCardHeader>

      <IonCardContent>
        <div style={fieldRow}>
          <span style={lbl}>Issued On</span>
          <span style={val}>{fmt(caItem.issued)}</span>
        </div>
        <div style={fieldRow}>
          <span style={lbl}>Issued By</span>
          <span style={val}>{caItem.issuedByName}</span>
        </div>
        <div style={fieldRow}>
          <span style={lbl}>Validation Status</span>
          <span style={{ ...val, color: validationStatusColor, fontWeight: validationStatusId !== 0 ? 600 : 'normal' }}>
            {validationStatusName || '—'}
          </span>
        </div>
        <div style={fieldRow}>
          <span style={lbl}>Validation Comments</span>
          <span style={val}>{caItem.validationComments ?? '—'}</span>
        </div>
        <div style={fieldRow}>
          <span style={lbl}>Validated On</span>
          <span style={val}>{fmt(caItem.validatedOn)}</span>
        </div>

        {/* Responses toggle */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: '12px',
            cursor: 'pointer',
            paddingTop: '8px',
            borderTop: '1px solid var(--ion-color-light-shade)',
          }}
          onClick={handleToggle}
        >
          <IonText style={{ fontSize: '0.85rem', fontWeight: '600' }}>
            Responses{responses !== null ? ` (${responses.length})` : ''}
          </IonText>
          <IonIcon icon={expanded ? chevronUpOutline : chevronDownOutline} color="medium" />
        </div>

        {expanded && (
          <div style={{ marginTop: '8px' }}>
            {loadingResponses ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0' }}>
                <IonSpinner name="dots" />
                <IonText color="medium" style={{ fontSize: '0.85rem' }}>Loading…</IonText>
              </div>
            ) : !responses || responses.length === 0 ? (
              <IonText color="medium" style={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                No responses on record.
              </IonText>
            ) : (
              responses.map(r => (
                <div
                  key={r.Id}
                  style={{
                    background: 'var(--ion-color-light-tint)',
                    borderRadius: '6px',
                    padding: '8px',
                    marginBottom: '6px',
                    fontSize: '0.85rem',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontWeight: '600' }}>{r.StatusName}</span>
                    <span style={{ color: 'var(--ion-color-medium)', fontSize: '0.8rem' }}>
                      {fmt(r.Date)} — {r.Username}
                    </span>
                  </div>
                  <div>{r.Comments}</div>
                </div>
              ))
            )}
          </div>
        )}
      </IonCardContent>
    </IonCard>
  );
};

export default CACard;
